import { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  getDocuments, 
  uploadDocument, 
  updateDocument,
  toggleDocumentVisibility, 
  deleteDocument,
  getDocumentCategories,
  getStorageConfig,
  updateStorageConfig,
  getTCAgreements,
  downloadTCAgreementPDF
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  Plus, Trash2, FileText, Loader2, MoreHorizontal, Eye, EyeOff, Edit,
  Download, Upload, Search, FolderOpen, Cloud, HardDrive, Info,
  FileCheck, AlertCircle, Settings, Shield, User, Calendar, Globe
} from 'lucide-react';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (mimeType) => {
  if (mimeType?.includes('pdf')) return '📄';
  if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '📊';
  if (mimeType?.includes('image')) return '🖼️';
  return '📁';
};

const isImageFile = (mimeType) => mimeType?.includes('image');
const isPdfFile = (mimeType) => mimeType?.includes('pdf');
const isOfficeFile = (mimeType) => 
  mimeType?.includes('word') || 
  mimeType?.includes('document') || 
  mimeType?.includes('excel') || 
  mimeType?.includes('spreadsheet') ||
  mimeType?.includes('powerpoint') ||
  mimeType?.includes('presentation');

const getPreviewUrl = (doc) => {
  // Use the preview endpoint for inline viewing (no download dialog)
  return `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/preview`;
};

const getDownloadUrl = (doc) => {
  return `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`;
};

export default function AdminRepository() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const tenantId = outletContext.tenantId;
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [storageConfig, setStorageConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  
  // Tabs
  const [activeTab, setActiveTab] = useState('documents');
  
  // T&C Agreements
  const [tcAgreements, setTcAgreements] = useState([]);
  const [tcLoading, setTcLoading] = useState(false);
  const [tcPage, setTcPage] = useState(1);
  const [tcTotal, setTcTotal] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  
  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadVisible, setUploadVisible] = useState(true);
  
  const fileInputRef = useRef(null);

  const fetchData = async () => {
    try {
      const [docsRes, catsRes, configRes] = await Promise.all([
        getDocuments(),
        getDocumentCategories(),
        getStorageConfig()
      ]);
      setDocuments(docsRes.data);
      setCategories(catsRes.data);
      setStorageConfig(configRes.data);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTCAgreements = async (page = 1) => {
    try {
      setTcLoading(true);
      // Fetch agreements - if no tenantId, fetch all (for debugging)
      const response = await getTCAgreements(tenantId || '', page, 50);
      setTcAgreements(response.data.agreements || []);
      setTcTotal(response.data.total || 0);
      setTcPage(page);
    } catch (error) {
      console.error('Failed to fetch T&C agreements:', error);
      toast.error('Failed to load T&C agreements');
    } finally {
      setTcLoading(false);
    }
  };

  const handleDownloadAgreement = async (agreement) => {
    try {
      const response = await downloadTCAgreementPDF(agreement.agreement_id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TC_Agreement_${agreement.user_name.replace(/\s+/g, '_')}_${agreement.agreement_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Agreement downloaded');
    } catch (error) {
      console.error('Failed to download agreement:', error);
      toast.error('Failed to download agreement');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'tc-agreements' && tenantId) {
      fetchTCAgreements(1);
    }
  }, [activeTab, tenantId]);

  const filteredDocuments = documents.filter(doc => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!doc.original_filename?.toLowerCase().includes(query) &&
          !doc.description?.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (filterCategory !== 'all' && doc.category !== filterCategory) return false;
    if (filterVisibility !== 'all') {
      const isVisible = filterVisibility === 'visible';
      if (doc.is_visible !== isVisible) return false;
    }
    return true;
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setIsUploading(true);
    try {
      await uploadDocument(uploadFile, {
        description: uploadDescription,
        category: uploadCategory || 'other',
        is_visible: uploadVisible
      });
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      resetUploadForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadDescription('');
    setUploadCategory('');
    setUploadVisible(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleToggleVisibility = async (doc) => {
    try {
      await toggleDocumentVisibility(doc.id);
      toast.success(`Document ${doc.is_visible ? 'hidden' : 'visible'} to users`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handleDelete = async (doc) => {
    if (!confirm(`Delete "${doc.original_filename}"? This cannot be undone.`)) return;
    try {
      await deleteDocument(doc.id);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleUpdateDocument = async (e) => {
    e.preventDefault();
    if (!editingDoc) return;
    
    try {
      await updateDocument(editingDoc.id, {
        description: editingDoc.description,
        category: editingDoc.category
      });
      toast.success('Document updated');
      setEditingDoc(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleStorageChange = async (storageType) => {
    try {
      await updateStorageConfig(storageType);
      toast.success(`Storage changed to ${storageType}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update storage');
    }
  };

  const getCategoryLabel = (value) => {
    return categories.find(c => c.value === value)?.label || value;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-repository">
      {/* Tabs for Documents and T&C Agreements */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="tc-agreements" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            T&C Agreements
            {tcTotal > 0 && <Badge variant="secondary" className="ml-1">{tcTotal}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6 mt-6">
          {/* Storage Settings Info */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle className="flex items-center justify-between">
              <span>Document Storage: {storageConfig?.active_storage?.toUpperCase() || 'LOCAL'}</span>
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Configure Storage
              </Button>
            </AlertTitle>
            <AlertDescription className="text-sm text-muted-foreground mt-1">
              {storageConfig?.instructions?.[storageConfig?.active_storage] || 'Documents are stored locally.'}
            </AlertDescription>
          </Alert>

      {/* Header & Filters */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading">Quality Documents</CardTitle>
              <CardDescription>
                Upload and manage quality guidelines, compliance documents, and training materials.
                The AI will use these documents to refine its scoring logic.
              </CardDescription>
            </div>
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" onClick={resetUploadForm}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Document</DialogTitle>
                  <DialogDescription>
                    Upload quality guidelines or compliance documents. Supported formats: PDF, DOC, DOCX, TXT
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4">
                  <div className="space-y-2">
                    <Label>File *</Label>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.txt,.xlsx,.xls"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      className="cursor-pointer"
                    />
                    {uploadFile && (
                      <p className="text-sm text-muted-foreground">
                        Selected: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={uploadCategory} onValueChange={setUploadCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={uploadDescription}
                      onChange={(e) => setUploadDescription(e.target.value)}
                      placeholder="Brief description of the document..."
                      rows={3}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="visible"
                      checked={uploadVisible}
                      onChange={(e) => setUploadVisible(e.target.checked)}
                      className="rounded"
                    />
                    <Label htmlFor="visible" className="cursor-pointer">
                      Visible to users (they can view/download this document)
                    </Label>
                  </div>
                  <Button type="submit" className="w-full btn-primary" disabled={isUploading || !uploadFile}>
                    {isUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Upload Document
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterVisibility} onValueChange={setFilterVisibility}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="visible">Visible</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getFileIcon(doc.mime_type)}</span>
                      <div>
                        <p className="font-medium">{doc.original_filename}</p>
                        {doc.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1">{doc.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{getCategoryLabel(doc.category)}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatFileSize(doc.file_size)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={doc.is_visible ? "default" : "secondary"}>
                      {doc.is_visible ? (
                        <><Eye className="h-3 w-3 mr-1" /> Visible</>
                      ) : (
                        <><EyeOff className="h-3 w-3 mr-1" /> Hidden</>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(doc.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setPreviewDoc(doc)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`, '_blank')}>
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditingDoc({...doc})}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleVisibility(doc)}>
                          {doc.is_visible ? (
                            <><EyeOff className="h-4 w-4 mr-2" /> Hide from Users</>
                          ) : (
                            <><Eye className="h-4 w-4 mr-2" /> Show to Users</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(doc)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredDocuments.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {documents.length === 0 
                  ? "No documents uploaded yet. Upload quality guidelines to help the AI score calls."
                  : "No documents match your filters."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* T&C Agreements Tab */}
        <TabsContent value="tc-agreements" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Terms & Conditions Agreements
                  </CardTitle>
                  <CardDescription>
                    View all signed T&C agreements from users. Each agreement includes digital signature, timestamp, and IP address.
                  </CardDescription>
                </div>
                <Badge variant="secondary" className="text-lg px-3 py-1">
                  {tcTotal} Total
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {tcLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : tcAgreements.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    No T&C agreements signed yet. Users will be prompted to accept terms when they first log in.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Digital Signature</TableHead>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead className="text-right">Agreement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tcAgreements.map((agreement) => (
                      <TableRow key={agreement.agreement_id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">{agreement.user_name}</p>
                              <p className="text-xs text-muted-foreground">{agreement.user_email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {agreement.typed_name}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm">
                                {new Date(agreement.accepted_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(agreement.accepted_at).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-muted-foreground" />
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {agreement.ip_address || 'N/A'}
                            </code>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadAgreement(agreement)}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {/* Pagination */}
              {tcTotal > 50 && (
                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={tcPage === 1}
                    onClick={() => fetchTCAgreements(tcPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {tcPage} of {Math.ceil(tcTotal / 50)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={tcPage >= Math.ceil(tcTotal / 50)}
                    onClick={() => fetchTCAgreements(tcPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Document Dialog */}
      <Dialog open={!!editingDoc} onOpenChange={(open) => !open && setEditingDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
          </DialogHeader>
          {editingDoc && (
            <form onSubmit={handleUpdateDocument} className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-1">File</p>
                <p className="text-muted-foreground">{editingDoc.original_filename}</p>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={editingDoc.category} 
                  onValueChange={(v) => setEditingDoc({...editingDoc, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingDoc.description || ''}
                  onChange={(e) => setEditingDoc({...editingDoc, description: e.target.value})}
                  placeholder="Brief description..."
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full btn-primary">
                Save Changes
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Storage Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Document Storage Settings</DialogTitle>
            <DialogDescription>
              Choose where to store uploaded documents. Currently, only local storage is available.
              Cloud storage options will require additional configuration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Local Storage */}
            <Card className={`p-4 cursor-pointer transition-all ${storageConfig?.active_storage === 'local' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-muted/50'}`}
                  onClick={() => handleStorageChange('local')}>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-muted rounded-lg">
                  <HardDrive className="h-6 w-6 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">Local Storage</h4>
                    {storageConfig?.active_storage === 'local' && (
                      <Badge variant="default">Active</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Documents are stored on the server. Simple and reliable.
                  </p>
                </div>
                <FileCheck className={`h-5 w-5 ${storageConfig?.active_storage === 'local' ? 'text-blue-500' : 'text-transparent'}`} />
              </div>
            </Card>

            {/* OneDrive Storage (Future) */}
            <Card className="p-4 opacity-60">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-muted rounded-lg">
                  <Cloud className="h-6 w-6 text-blue-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">Microsoft OneDrive</h4>
                    <Badge variant="secondary">Coming Soon</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Store documents in OneDrive. Requires Azure configuration.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    To enable: Configure AZURE_CLIENT_ID, AZURE_CLIENT_SECRET in .env
                  </p>
                </div>
              </div>
            </Card>

            {/* Google Drive Storage (Future) */}
            <Card className="p-4 opacity-60">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-muted rounded-lg">
                  <FolderOpen className="h-6 w-6 text-yellow-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">Google Drive</h4>
                    <Badge variant="secondary">Coming Soon</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Store documents in Google Drive. Requires Google API configuration.
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    To enable: Configure GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET in .env
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={(open) => !open && setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {previewDoc?.original_filename}
            </DialogTitle>
            <DialogDescription>
              {getCategoryLabel(previewDoc?.category)} • {formatFileSize(previewDoc?.file_size || 0)}
            </DialogDescription>
          </DialogHeader>
          
          {previewDoc && (
            <div className="flex-1 overflow-auto">
              {isImageFile(previewDoc.mime_type) ? (
                <div className="flex items-center justify-center p-4">
                  <img 
                    src={getPreviewUrl(previewDoc)} 
                    alt={previewDoc.original_filename}
                    className="max-w-full max-h-[60vh] object-contain rounded-lg"
                  />
                </div>
              ) : isPdfFile(previewDoc.mime_type) ? (
                <iframe
                  src={getPreviewUrl(previewDoc)}
                  className="w-full h-[60vh] rounded-lg border"
                  title={previewDoc.original_filename}
                />
              ) : isOfficeFile(previewDoc.mime_type) ? (
                <div className="flex flex-col items-center justify-center p-8 text-center bg-muted/50 rounded-lg">
                  <div className="text-6xl mb-4">{getFileIcon(previewDoc.mime_type)}</div>
                  <h3 className="text-lg font-medium mb-2">{previewDoc.original_filename}</h3>
                  <p className="text-muted-foreground mb-4">
                    Office documents cannot be previewed directly in the browser.
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={() => window.open(getDownloadUrl(previewDoc), '_blank')}>
                      <Download className="h-4 w-4 mr-2" />
                      Download to View
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => window.open(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(getDownloadUrl(previewDoc))}`, '_blank')}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Open in Office Online
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-8 text-center bg-muted/50 rounded-lg">
                  <div className="text-6xl mb-4">{getFileIcon(previewDoc.mime_type)}</div>
                  <h3 className="text-lg font-medium mb-2">{previewDoc.original_filename}</h3>
                  <p className="text-muted-foreground mb-4">
                    This file type cannot be previewed in the browser.
                  </p>
                  <Button onClick={() => window.open(getDownloadUrl(previewDoc), '_blank')}>
                    <Download className="h-4 w-4 mr-2" />
                    Download File
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
